import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Col, Row } from "react-bootstrap";
import "./generate-report-modal.scss";
import ReportsService from "../../../../../../../../../services/reports-service";

const GenerateReportModal = ({ show, onClose, reports }) => {
  const reportsService = new ReportsService();

  const initialFormData = {
    addresseeTitle: "Mr.",
    firstName: "",
    middleInitial: "",
    lastName: "",
    designation: "",
    companyName: "",
    address: "",
    referenceNumber: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Reset form and error state whenever modal is opened
  useEffect(() => {
    if (show) {
      setFormData(initialFormData);
      setShowSuccessModal(false);
      setShowErrorModal(false);
      setErrorMessage("");
    }
  }, [show]);

  // Individual field validity
  const allFilled = [
    formData.addresseeTitle,
    formData.firstName,
    formData.lastName,
    formData.designation,
    formData.companyName,
    formData.address,
  ].every((field) => field.trim().length > 0);

  const refValid =
    formData.referenceNumber.length <= 30 && !/\s/.test(formData.referenceNumber);

  const isFormValid = allFilled && refValid;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "referenceNumber") {
      // strip out any whitespace characters
      newValue = value.replace(/\s/g, "");
    }
    setFormData({ ...formData, [name]: newValue });
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      setErrorMessage(
        "Please fill out all required fields (except Middle Initial) and ensure Reference Number is 30 characters or less with no spaces."
      );
      setShowErrorModal(true);
      return;
    }
  
    const requestData = {
      institutionId: reports[0].institutionId,
      issuerRequestDate: reports[0].issuerRequestDate,
      reportType: reports[0].reportType,
      addressee: [
        {
          addresseeTitle: formData.addresseeTitle,
          firstName: formData.firstName,
          middleInitial: formData.middleInitial,
          lastName: formData.lastName,
        },
      ],
      address: formData.address,
      companyName: formData.companyName,
      designation: formData.designation,
      referenceNumber: formData.referenceNumber,
    };
  
    try {
      const response = await reportsService.rrMakerGenerateAuditConfirmation(
        requestData.institutionId,
        requestData.issuerRequestDate,
        requestData.reportType,
        requestData.addressee,
        requestData.address,
        requestData.companyName,
        requestData.designation,
        requestData.referenceNumber
      );
      const { message, updatedAttributes } = response;

      if (updatedAttributes) {
        onClose();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setErrorMessage(message || "Unknown error");
        setShowErrorModal(true);
      }
    } catch (error) {
      const caughtMsg =
        error?.response?.data?.message || error?.message || "Error submitting the report.";
      setErrorMessage(caughtMsg);
      setShowErrorModal(true);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered className="generate-report-modal">
        <Modal.Header closeButton>
          <Modal.Title>Generate Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Addressee Row */}
            <Row className="mb-3 align-items-center">
              <Col xs="auto">
                <Form.Label className="mb-0">Addressee</Form.Label>
              </Col>
              <Col xs="auto">
                <Form.Control
                  as="select"
                  name="addresseeTitle"
                  size="sm"
                  className="mt-3 form-select"
                  onChange={handleChange}
                  value={formData.addresseeTitle}
                >
                  <option>Mr.</option>
                  <option>Ms.</option>
                  <option>Mrs.</option>
                </Form.Control>
              </Col>
              <Col xs={5}>
                <Form.Control
                  type="text"
                  name="firstName"
                  placeholder="Enter First Name"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.firstName}
                />
              </Col>
              <Col xs="auto">
                <Form.Control
                  type="text"
                  name="middleInitial"
                  placeholder="M (optional)"
                  maxLength={1}
                  size="sm"
                  className="middle-initial-input"
                  onChange={handleChange}
                  value={formData.middleInitial}
                />
              </Col>
            </Row>

            {/* Last Name */}
            <Row className="mb-3 align-items-center">
              <Col xs={{ span: 4, offset: 2 }}>
                <Form.Control
                  type="text"
                  name="lastName"
                  placeholder="Enter Last Name"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.lastName}
                />
              </Col>
            </Row>

            {/* Other Fields */}
            <Form.Group className="mb-3">
              <Form.Label>Designation</Form.Label>
              <Form.Control
                type="text"
                name="designation"
                placeholder="Enter Designation"
                maxLength={100}
                onChange={handleChange}
                value={formData.designation}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                placeholder="Enter Company Name"
                maxLength={100}
                onChange={handleChange}
                value={formData.companyName}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                placeholder="Enter Address"
                maxLength={250}
                onChange={handleChange}
                value={formData.address}
              />
            </Form.Group>

            {/* Reference Number */}
            <Form.Group className="mb-3">
              <Form.Label>Reference Number</Form.Label>
              <Form.Control
                type="text"
                name="referenceNumber"
                placeholder="Enter Reference Number"
                onChange={handleChange}
                value={formData.referenceNumber}
                isInvalid={!refValid}
              />
              {/* <Form.Text className={`text-${formData.referenceNumber.length <= 30 ? "muted" : "danger"}`}>
                {formData.referenceNumber.length}/30 characters (no spaces)
              </Form.Text> */}
              <Form.Control.Feedback type="invalid">
                Reference Number must be 30 characters or less with no spaces.
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Report is generating. Refresh in a few minutes!</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => window.location.reload()}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GenerateReportModal;
